"use client";

/**
 * A listing or residence photo that never flickers in: a quiet frame
 * holds the space, the photo fades in once it has loaded, and a photo
 * the browser already holds (this session, or the card just tapped)
 * paints at once with no fade. Before, each surface popped its photo
 * in on its own terms; the listing drawer went dark, then blurred, then
 * sharp (owner, 2026-10-11: "make sure everything else that loads
 * listing or residence images is the same way").
 *
 * Server-rendered pages: the <img> is visible in the HTML (no opacity
 * trick), so a photo that lands before hydration shows and the LCP is
 * untouched; the fade only applies to photos still loading at mount.
 * A priority photo (eager or fetchPriority high: the hero, the first
 * cards) never fades: once the pages hydrated sooner (2026-10-19, less
 * JS up front), hydration landed before the file and hiding it then
 * added the whole wait to the residence page's LCP.
 *
 * Plain <img>, not next/image: the hosts (bina, emlak, the media route)
 * are many and every caller passed `unoptimized` anyway.
 */
import { useLayoutEffect, useRef, useState } from "react";

/** Photos that finished loading this session: painted at once, no fade. */
const loadedSrcs = new Set<string>();

export interface PhotoProps extends Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src" | "alt" | "className" | "onLoad" | "onError"
> {
  src: string;
  alt?: string;
  /** Classes for the <img> itself (hover zoom, object-contain). */
  className?: string;
  /** Classes for the frame: size, radius, position. The default fills a
   *  positioned parent. The frame is positioned itself unless the classes
   *  already place it. */
  frame?: string;
  /** Tried when the first file fails (bina's card size falls back to the
   *  original). */
  fallbackSrc?: string | null;
  /** Rendered in the frame when the photo fails and there is nothing
   *  else to try. */
  fallback?: React.ReactNode;
  /** Called once the photo (and any fallback file) has failed. */
  onBroken?: () => void;
  /** <source> elements for a <picture> (a phone-sized variant). When the
   *  chosen variant fails, the plain src is tried without them. */
  sources?: React.ReactNode;
  /** Width candidates of `src` (the API's resized card copies). They go
   *  with the first attempt only: with a srcset on the <img> a new src is
   *  never fetched, so the fallback file needs them gone. */
  srcSet?: string;
  sizes?: string;
}

type Phase = "idle" | "loading" | "shown";

export default function Photo({
  src,
  alt = "",
  className = "",
  frame = "absolute inset-0",
  fallbackSrc = null,
  fallback = null,
  onBroken,
  sources,
  srcSet,
  sizes,
  loading = "lazy",
  decoding = "async",
  draggable = false,
  ...rest
}: PhotoProps) {
  const ref = useRef<HTMLImageElement>(null);
  const [current, setCurrent] = useState(src);
  const [phase, setPhase] = useState<Phase>(() => (loadedSrcs.has(src) ? "shown" : "idle"));
  const [broken, setBroken] = useState(false);
  const [withSources, setWithSources] = useState(!!sources);
  const priority = loading === "eager" || rest.fetchPriority === "high";

  // A new src: start over.
  if (current !== src && !(fallbackSrc && current === fallbackSrc)) {
    setCurrent(src);
    setPhase(loadedSrcs.has(src) ? "shown" : "idle");
    setBroken(false);
  }

  // Before the first paint: a photo the browser already has shows at
  // once; one still loading gets the fade. Runs on hydration too, so a
  // server-rendered photo that landed early is never hidden.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || phase === "shown") return;
    if (el.complete && el.naturalWidth > 0) {
      loadedSrcs.add(current);
      setPhase("shown");
      return;
    }
    if (el.complete && el.naturalWidth === 0 && el.getAttribute("src")) {
      // Finished with no size: the file failed before React listened.
      fail();
      return;
    }
    // Still loading: fade it in, unless it is the page's priority photo,
    // which stays visible and paints as it arrives.
    if (!priority) setPhase("loading");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  function fail() {
    if (withSources) {
      setWithSources(false);
      setPhase("idle");
      return;
    }
    if (fallbackSrc && current !== fallbackSrc) {
      setCurrent(fallbackSrc);
      setPhase("idle");
      return;
    }
    setBroken(true);
    onBroken?.();
  }

  const first = current === src;
  const positioned = /\b(absolute|fixed)\b/.test(frame);
  const display = /\b(hidden|block|inline-block|inline|flex|grid)\b/.test(frame) ? "" : "block";
  const fit = /\bobject-/.test(className) ? "" : "object-cover";

  const img = (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={ref}
      src={current}
      srcSet={first ? srcSet : undefined}
      sizes={first ? sizes : undefined}
      alt={alt}
      loading={loading}
      decoding={decoding}
      draggable={draggable}
      onLoad={() => {
        loadedSrcs.add(current);
        setPhase("shown");
      }}
      onError={fail}
      className={`absolute inset-0 h-full w-full ${fit} ${className} ${
        phase === "loading" ? "opacity-0" : "opacity-100"
      } ${phase === "idle" ? "" : "transition-opacity duration-200 ease-out"}`}
      {...rest}
    />
  );

  return (
    <span className={`${frame} ${positioned ? "" : "relative"} ${display} overflow-hidden`}>
      {phase !== "shown" && !broken && (
        <span aria-hidden className="absolute inset-0 animate-pulse bg-gray-200" />
      )}
      {broken ? fallback : withSources && sources ? <picture>{sources}{img}</picture> : img}
    </span>
  );
}
